import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { Button, Dropdown } from 'react-bootstrap5';
import TextareaAutosize from 'react-textarea-autosize';
import { PubSub } from 'aws-amplify';

import routes from 'app/routes';
import LoadingPlaceholder from 'components/new_ui/WaitLayers/LoadingPlaceholder';
import IconWithText from 'components/Widgets/IconWithText';
import ExternalLink from 'components/Widgets/ExternalLink';
import CoachIcon from 'components/Common/Coaches/Widgets/Icon/Index';
import useChat from 'components/Common/Chats/Hooks/useChat';
import useMessages from 'components/Common/Chats/Hooks/useMessages';
import useIot from 'components/Common/Iot/Hooks/useIot';
import { useContext as useUsersContext } from 'components/Users/Context';
import Alert from 'components/new_ui/Alerts/alert';

import FileMessage from './Messages/FileMessage';
import TextMessage from './Messages/TextMessage';
import ImageMessage from './Messages/ImageMessage';
import VideoMessage from './Messages/VideoMessage';
import NotificationMessage from './Messages/NotificationMessage';
import SelectedFilePreview from './SelectedFilePreview';
import actions from './Actions';

export default function Messages({ updateCountUnreadMessage }) {
  const navigate = useNavigate();

  const { chatId } = useParams();
  const { setChat } = useOutletContext();

  const inputFileRef = useRef(null);

  const { messages, addMessage } = useMessages({ chatId });
  const { chat, refresh } = useChat({ chatId });

  const [inputMessage, setInputMessage] = useState('');
  const [inputFile, setInputFile] = useState(null);

  const { state: stateUsers } = useUsersContext();

  const isUserBlocked = stateUsers.user?.attributes?.specific?.status === 'blocked';
  const [disabled, setDisabled] = useState(isUserBlocked);

  useIot();

  const backToList = () => {
    setChat(null);
    navigate(routes.members.chats.index);
  };

  const scrollToBottom = () => {
    const mlistElement = document.getElementsByClassName('message-list')[0];
    if (typeof mlistElement !== 'undefined') {
      mlistElement.scrollTop = mlistElement.scrollHeight;
    }
  };

  useEffect(() => {
    let sub = null;
    if (stateUsers.user) {
      sub = PubSub.subscribe(`user-${stateUsers.user.attributes.token}`).subscribe({
        next: ({ value }) => {
          if (value.type === 'chat.room') {
            if (parseInt(value.message.session_room.id, 10) === parseInt(chatId, 10)) {
              addMessage(value.message.message);
            } else {
              updateCountUnreadMessage();
            }
          }
        },
      });
    }

    return () => {
      if (sub) {
        sub.unsubscribe();
      }
    };
  }, [stateUsers.user, chatId]);

  useEffect(() => {
    setTimeout(scrollToBottom, 500);
  }, [messages]);

  useEffect(() => {
    if (messages) {
      const promises = [];

      messages.forEach((message) => {
        if (!message.isRead) {
          promises.push(actions.readMessage(chatId, message.realId));
        }
      });

      if (promises.length > 0) {
        Promise.all(promises).then((_values) => {
          updateCountUnreadMessage();
        });
      }
    }
  }, [messages]);

  if (messages === null || chat === null) {
    return (
      <LoadingPlaceholder className="mt-3" text="Loading..." />
    );
  }

  const groupMessages = messages.reduce((groupedMessages, message) => {
    const date = new Date(message.created).toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
    if (!groupedMessages[date]) {
      groupedMessages[date] = [];
    }
    groupedMessages[date].push(message);
    return groupedMessages;
  }, {});

  const onSubmit = () => {
    setDisabled(true);

    actions.sendMessage(
      chatId,
      {
        body: inputMessage,
        file: inputFile,
      },
      () => {
        setInputMessage('');
        setInputFile(null);
        setDisabled(false);
      },
      (errorsMessage) => {
        setDisabled(false);
        Alert({
          text: errorsMessage,
        });
      },
    );
  };

  const archiveChat = () => {
    actions.archiveChat(chatId, () => {
      updateCountUnreadMessage();
      refresh();
    });
  };

  return (
    <>
      <div className="chat-info d-flex justify-content-between align-items-center">
        <Button onClick={backToList} variant="link" className="d-md-none">
          <IconWithText iconClassName="arrow-left-icon small-icon bg-white" />
        </Button>

        <ExternalLink
          variant={null}
          className="text-decoration-none"
          href={routes.coaches.show(chat.attributes.coach_id)}
        >
          <CoachIcon
            coach={{
              avatar_url: chat.links.coach_avatar,
            }}
            iconClassName="coach-info"
            bigIcon={false}
          >
            <small className="text-gray-900">
              {chat.attributes.coach}
            </small>
          </CoachIcon>
        </ExternalLink>

        <Dropdown className="position-static">
          <Dropdown.Toggle variant="" className="btn-link">
            <div className="icon-with-text">
              <i className="small-icon options-icon" />
            </div>
          </Dropdown.Toggle>

          <Dropdown.Menu align="end">
            {chat.attributes.last_program_id && (
              <Dropdown.Item key="program" href={routes.members.programs.show(chat.attributes.last_program_id)}>
                Development Plan
              </Dropdown.Item>
            )}

            <Dropdown.Item key="archive-active" onClick={archiveChat}>
              {chat.attributes.student_archived_at === null ? 'Archive Contact' : 'Activate Contact'}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="message-list">
        {Object.keys(groupMessages).map((date) => (
          <>
            <div className="chat-date">
              {date}
            </div>
            {groupMessages[date].map((message) => {
              switch (message.type) {
                case 'system':
                  return (
                    <NotificationMessage
                      key={message.id}
                      message={message}
                    />
                  );
                case 'photo':
                  return (
                    <ImageMessage
                      key={message.id}
                      message={message}
                    />
                  );
                case 'video':
                  return (
                    <VideoMessage
                      key={message.id}
                      message={message}
                    />
                  );
                case 'file':
                  return (
                    <FileMessage
                      key={message.id}
                      message={message}
                    />
                  );
                default:
                  return (
                    <TextMessage
                      key={message.id}
                      message={message}
                    />
                  );
              }
            })}
          </>
        ))}
      </div>
      <div className="chat-input-wrapper">
        <div className="rce-container-input gap-2">
          <TextareaAutosize
            minRows={1}
            maxRows={3}
            className="rce-input"
            value={inputMessage}
            placeholder="Send a message..."
            onChange={(event) => {
              if (event.target) {
                setInputMessage(event.target.value);
              }
            }}
            disabled={disabled}
          />
          <div className="rce-input-buttons gap-2">
            <Button
              disabled={disabled}
              variant="outline-primary"
              className="btn-outline-icon"
              onClick={() => inputFileRef.current.click()}
            >
              <IconWithText
                iconClassName="small-icon attachment-icon"
              />
            </Button>
            <Button disabled={disabled} className="btn-icon" onClick={() => onSubmit()}>
              {disabled && !isUserBlocked ? <LoadingPlaceholder /> : (
                <IconWithText
                  iconClassName="small-icon arrow-up-icon bg-white"
                />
              )}
            </Button>
            <input
              ref={inputFileRef}
              className="d-none"
              type="file"
              onChange={(event) => {
                if (event.target) {
                  setInputFile(event.target.files[0]);
                }
              }}
            />
          </div>
        </div>
        <SelectedFilePreview disabled={disabled} file={inputFile} removeFile={() => setInputFile(null)} />

      </div>

    </>
  );
}

Messages.propTypes = {
  updateCountUnreadMessage: PropTypes.func.isRequired,
};
